import { CSSProperties, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@arco-design/web-react';
import type { ButtonProps } from '@arco-design/web-react/es/Button';
import usePopupTopLayer from '../hooks/usePopupTopLayer';

/**
 * 壳只需要「读全量字段值」这一项能力，故按结构取最小类型：Arco 的 `FormInstance<T>` 泛型在各页
 * 各不相同（`FormInstance<TrunkForm>` 等），写成具体类型会在 `getFieldsValue` 参数逆变处报错。
 */
export interface FormModalFormLike {
  getFieldsValue?: (...args: never[]) => unknown;
}

/**
 * 表单弹窗宽度档位（按**字段数与内容形态**取档，docs/design/design-spec.md §3.3）：
 * - `compact` 480：≤3 个字段的窄伴随表单、二次确认类弹窗；
 * - `form`    560：常规表单弹窗（默认值）；
 * - `wide`    720：分区长表单（8–14 字段，可两列铺开）；
 * - `xwide`   880：字段成组、需两列铺开的超长表单（≥15 字段，如机器人配置、Q&A 编辑）；
 * - `sheet`  1000：内容驱动大弹层（弹窗内嵌多张表格/画布/流式输出，不是常规表单）。
 * 页面**禁止**再写裸数字宽度（门禁 `scripts/check-popup-shell.mjs` R5）；超过 1000 的超大弹层按页登记豁免。
 * 全局 `max-width: 94vw` 由 components.css 兜底，窄屏不会溢出视口。
 */
export const FORM_MODAL_WIDTH = {
  compact: 480,
  form: 560,
  wide: 720,
  xwide: 880,
  sheet: 1000,
} as const;

/** 确定按钮语义色，与操作列 ConfirmButton 语义一致：danger=破坏性、warning=谨慎变更、success=正向推进 */
export type FormModalStatus = 'success' | 'warning' | 'danger';

/** 校验失败时被 Arco Form 标红的字段容器（待修正计数与滚动定位的唯一锚点） */
const ERROR_ITEM_SELECTOR = '.arco-form-item-status-error';

/** 字段值快照：顶层键排序后再序列化，避免字段注册顺序变化被误判成「有改动」 */
function snapshotValues(values?: Record<string, unknown>) {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(values || {}).sort()) out[key] = (values as Record<string, unknown>)[key];
  return JSON.stringify(out);
}

/**
 * 通用「表单弹窗壳」：统一新建/编辑表单弹窗的 Modal 骨架样板
 * （40+ 文件重复的 `unmountOnExit` + `className="bcc-scroll-modal"` + 宽度分档 + loading/footer 语义）。
 *
 * 边界与约定：
 * - 只做外层 Modal 壳，children 原样透传——既支持「children 为自带 `<Form>` 的 Form.Item 区域」，
 *   也支持「children 即完整内容」两种用法；壳不包 Form、不持 form 实例（避免过度设计）。
 * - footer 缺省 = Arco 默认「取消 + 确定」：确定按钮在 `confirmLoading` 或 `onOk` 返回 Promise 期间
 *   自动 loading；`footer={null}` 隐藏（查看类）、传入节点则整体替换。传 `status`/`okButtonProps`
 *   可给确定按钮挂 danger/warning/success 语义。
 * - `onOk` 只负责触发，不自动关闭、不包 validate：校验/提交/Message/关闭/刷新由调用方在回调内完成
 *   （成功路径自行 `setVisible(false)` + 重新拉取），与存量页面行为完全一致。
 * - 关闭后默认卸载内容 DOM（`unmountOnExit`），表单重开时由调用方 open 流程 reset/setFieldsValue。
 * - 传 `form` 后壳额外接管两件事（见 `form` 字段注释）：关闭前的未保存保护、校验失败的定位与计数；
 *   两件事都基于字段值快照与正文 DOM，不介入调用方的校验/提交流程，不传 `form` 时零影响。
 */
export interface FormModalProps {
  title?: ReactNode;
  visible: boolean;
  /** 关闭入口：点遮罩 / Esc / 右上角 × / 取消按钮统一触发（与 Arco Modal 语义一致）；传 `form` 时先过未保存保护 */
  onCancel: () => void;
  /** 确定回调（默认 footer 的「确定」触发）；返回 Promise 时确定按钮自动 loading（Arco 原生联动） */
  onOk?: () => void | Promise<void>;
  /** 确定按钮 loading（提交期间由调用方置 true；与 onOk 返回 Promise 可同用，行为同裸 Modal） */
  confirmLoading?: boolean;
  /** 确定按钮文案（缺省走 Arco locale：确定） */
  okText?: ReactNode;
  /** 取消按钮文案（缺省走 Arco locale：取消） */
  cancelText?: ReactNode;
  /** 隐藏取消按钮（仅保留确定，如二次确认弹窗） */
  hideCancel?: boolean;
  /** 确定按钮语义色；传入时合入 okButtonProps 并优先生效 */
  status?: FormModalStatus;
  /** 覆盖确定按钮属性（type/disabled 等，status 冲突时以 status prop 为准） */
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  /** 弹窗宽度：优先本 prop，其次 style.width，缺省 form 档 560；可用 FORM_MODAL_WIDTH 档位或任意数值 */
  width?: number;
  /**
   * 表单实例（可选）。传入后壳获得两项能力，调用方不用改自己的校验/提交逻辑：
   * - **未保存保护**：字段值偏离打开时的基线后，关闭入口（遮罩 / Esc / × / 取消）先弹确认，避免误关丢输入；
   * - **校验失败可见**：点确定后若出现校验错误，自动把首个错误项滚入可视区，并在底栏左侧显示待修正项数
   *   （项数随用户修正实时刷新，改完自动消失）。
   * 基线在弹窗打开后一帧采集，因此调用方在 open 流程里 `setFieldsValue` 回填不会被判成「有改动」。
   */
  form?: FormModalFormLike;
  /** 关闭前检查未保存修改（默认 true；传了 `form` 或 `dirty` 时生效） */
  guardUnsaved?: boolean;
  /**
   * 页面自算的"有未保存修改"判据：给**不传 `form`** 的弹窗用（如自定义受控字段、富文本、级联选择）。
   * 此前这类弹窗关闭时零保护——点了遮罩就把输入的内容丢掉。
   */
  dirty?: boolean;
  /** 校验失败时滚到首个错误项（默认 true；仅传了 `form` 时生效） */
  scrollToError?: boolean;
  /** 关闭后卸载内容 DOM（默认 true，销毁表单残留与内部弹出层） */
  unmountOnExit?: boolean;
  /**
   * 主体限高滚动（默认 true → `bcc-scroll-modal` + 内层 `bcc-modal-body`，见 components.css）：
   * 限高加在**内层主体容器**上，不是 Arco 的 `.arco-modal-content`。
   * 原因：Arco Modal 把弹层（Select/Dropdown/DatePicker…）挂进 `.arco-modal-content`，该节点一旦
   * 带 `overflow:auto`，就同时是弹层的包含块——弹层会被裁剪到正文可视区内，表现为「下拉框展不开、
   * 只能看到一条」。改由内层容器承担滚动后，`.arco-modal-content` 保持 overflow 可见，弹层不再被裁。
   * max-height（非 height）保证短表单不触发、无副作用，长表单限高滚动时 header/footer 常驻。
   */
  scroll?: boolean;
  /** 覆盖底部（null 隐藏；函数形态同 Arco：传入 cancelButtonNode/okButtonNode 自行排布） */
  footer?: ReactNode | ((cancelButtonNode: ReactNode, okButtonNode: ReactNode) => ReactNode) | null;
  className?: string;
  style?: CSSProperties;
  /** 点遮罩是否关闭（默认 Arco 行为 true）；表单有未保存内容时传 false 防误关丢输入 */
  maskClosable?: boolean;
  /** Esc 是否关闭（默认 Arco 行为 true）；与 maskClosable 同用于防误关 */
  escToExit?: boolean;
  /** 是否显示右上角关闭按钮（默认 true）；强制流程弹窗传 false */
  closable?: boolean;
  children?: ReactNode;
}

export default function FormModal({
  title,
  visible,
  onCancel,
  onOk,
  confirmLoading,
  okText,
  cancelText,
  hideCancel,
  status,
  okButtonProps,
  cancelButtonProps,
  width,
  form,
  guardUnsaved = true,
  dirty,
  scrollToError = true,
  unmountOnExit = true,
  scroll = true,
  className,
  style,
  maskClosable,
  escToExit,
  closable,
  footer,
  children,
}: FormModalProps) {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  /** 正文容器节点：Arco Modal 的内容是打开后才挂载的，用回调 ref 拿节点，保证监听在挂载后才绑 */
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const attachBody = useCallback((node: HTMLDivElement | null) => {
    bodyRef.current = node;
    setBodyEl(node);
  }, []);
  /** 打开时的字段值基线（判断「是否改过」），空串 = 尚未采集 */
  const baselineRef = useRef('');
  /** 用户是否在正文里操作过（原生事件标记）：只有「人改过」才谈未保存，
   *  程序回填（编辑态异步 setFieldsValue、联动的默认值）不算，避免误弹确认框 */
  const touchedRef = useRef(false);
  const [errorCount, setErrorCount] = useState(0);

  // 基线延时一帧采集：调用方回填（setFieldsValue）多发生在 visible 置真后的 open 流程 / effect 里，
  // 而子组件 effect 先于父组件执行，立即取快照会把回填值误判成用户改动。
  useEffect(() => {
    if (!visible || !form) {
      baselineRef.current = '';
      touchedRef.current = false;
      setErrorCount(0);
      return;
    }
    const timer = window.setTimeout(() => {
      baselineRef.current = snapshotValues(form.getFieldsValue?.() as Record<string, unknown>);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [visible, form]);

  // 用户操作标记 + 待修正项数：原生事件在正文内冒泡（输入、下拉选择、单选点选均覆盖）。
  // 计数延到下一轮事件循环再读 DOM——此刻 Arco 才刚跑完 onChange 校验并清掉已修正项的报错。
  useEffect(() => {
    const node = bodyEl;
    if (!visible || !form || !node) return;
    let timer = 0;
    const sync = () => {
      touchedRef.current = true;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setErrorCount(node.querySelectorAll(ERROR_ITEM_SELECTOR).length), 0);
    };
    const events = ['input', 'change', 'click'] as const;
    events.forEach((ev) => node.addEventListener(ev, sync));
    return () => {
      window.clearTimeout(timer);
      events.forEach((ev) => node.removeEventListener(ev, sync));
    };
  }, [visible, form, bodyEl]);

  const isDirty = useCallback(() => {
    if (!form || !touchedRef.current || !baselineRef.current) return false;
    return snapshotValues(form.getFieldsValue?.() as Record<string, unknown>) !== baselineRef.current;
  }, [form]);

  /** 把首个错误项滚进正文可视区：优先滚我们自己接管的内层滚动容器，避免连带滚动背后的页面 */
  const revealFirstError = useCallback(() => {
    const node = bodyRef.current;
    if (!node) return;
    const first = node.querySelector(ERROR_ITEM_SELECTOR) as HTMLElement | null;
    if (!first) return;
    const box = node.getBoundingClientRect();
    const rect = first.getBoundingClientRect();
    if (rect.top >= box.top && rect.bottom <= box.bottom) return;
    if (node.scrollHeight > node.clientHeight + 4) node.scrollTop += rect.top - box.top - 16;
    else first.scrollIntoView({ block: 'center' });
  }, []);

  const handleOk = () => {
    const result = onOk?.();
    if (form && scrollToError) {
      // 校验错误提示在下一帧才渲染，此时再计数与定位；成功提交时弹窗已关闭，bodyRef 为空即整段跳过
      window.setTimeout(() => {
        const node = bodyRef.current;
        if (!node) return;
        setErrorCount(node.querySelectorAll(ERROR_ITEM_SELECTOR).length);
        revealFirstError();
      }, 120);
    }
    return result;
  };

  const handleCancel = () => {
    // 两种判据：传了 `form` 用值快照（只认"人改过"）；否则用页面自算的 `dirty`
    const unsaved = guardUnsaved && ((form ? isDirty() : false) || dirty === true);
    if (!unsaved) {
      onCancel();
      return;
    }
    Modal.confirm({
      title: '放弃未保存的修改？',
      content: '关闭后本次填写的内容不会保留。',
      okText: '放弃修改',
      cancelText: '继续编辑',
      okButtonProps: { status: 'warning' },
      onOk: () => onCancel(),
    });
  };

  // 打开时把本弹窗容器排到 body 末尾（见 hook 注释：从抽屉里再开的详情会被后插入的抽屉盖住时靠它兜底）
  const attachTopLayer = usePopupTopLayer(visible);
  // 宽度解析：width prop > style.width（兼容历史 style={{ width }} 写法）> 默认 form 档
  const styleWidth = style && style.width != null ? style.width : undefined;
  const resolvedWidth: CSSProperties['width'] =
    typeof width === 'number' ? width : (styleWidth ?? FORM_MODAL_WIDTH.form);
  const cls = [scroll ? 'bcc-scroll-modal' : '', className].filter(Boolean).join(' ') || undefined;

  // 底栏：调用方给了 footer 就原样用；否则传了 form 时在官方按钮左侧挂一条待修正提示（无错误即不渲染）
  const resolvedFooter =
    footer !== undefined
      ? footer
      : form
        ? (cancelButtonNode: ReactNode, okButtonNode: ReactNode) => (
            <div className="bcc-modal-foot">
              {errorCount > 0 && (
                <span className="bcc-modal-foot-tip" role="status">
                  还有 {errorCount} 项待修正
                </span>
              )}
              <span className="bcc-modal-foot-btns">
                {cancelButtonNode}
                {okButtonNode}
              </span>
            </div>
          )
        : undefined;

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={handleCancel}
      onOk={handleOk}
      confirmLoading={confirmLoading}
      okText={okText}
      cancelText={cancelText}
      hideCancel={hideCancel}
      okButtonProps={status ? { ...okButtonProps, status } : okButtonProps}
      cancelButtonProps={cancelButtonProps}
      unmountOnExit={unmountOnExit}
      maskClosable={maskClosable}
      escToExit={escToExit}
      closable={closable}
      className={cls}
      style={{ ...style, width: resolvedWidth }}
      footer={resolvedFooter}
    >
      {/* display:contents 中转节点：自身不生成盒子，只用来定位本弹窗的 Arco 容器 */}
      <div ref={attachTopLayer} style={{ display: 'contents' }}>
        {scroll ? (
          <div className="bcc-modal-body" ref={attachBody}>
            {children}
          </div>
        ) : (
          children
        )}
      </div>
    </Modal>
  );
}
